/**
 * Step5DeliveryRecipients Component
 *
 * Shows a table of all recipients who received the invite after marking as completed.
 * Also displays the sent email preview and guest URL events in a tabbed split view.
 * Displays: Name, Title, Organization in a sortable/searchable table.
 *
 * This step only appears when an invite is marked as completed.
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle2,
  Users,
  Building2,
  Search,
  Download,
  ChevronLeft,
  ArrowUpDown,
  Calendar,
  Mail,
  Undo2,
  FileText,
  Link2,
  ExternalLink,
} from 'lucide-react';
import type { InviteRecipient } from '../../types/emailTemplates';

interface Step5DeliveryRecipientsProps {
  /** Recipients who received the invite */
  recipients: InviteRecipient[];
  /** Invite name */
  inviteName: string;
  /** Completion date */
  completedAt: string;
  /** Navigation */
  onBack: () => void;
  /** Close modal */
  onDone: () => void;
  /** Undo mark as completed (revert to draft) */
  onUndoComplete?: () => void;
  /** Whether undo action is in progress */
  isUndoing?: boolean;
  /** The HTML email that was sent */
  sentEmailHtml?: string;
  /** The subject line that was sent */
  sentEmailSubject?: string;
  /** The guest link URL that was included */
  guestLinkUrl?: string;
}

type SortField = 'name' | 'title' | 'organization';
type SortDirection = 'asc' | 'desc';
type PreviewTab = 'email' | 'guestUrl';

/**
 * Format date for display (mm.dd.yyyy hh:mm am/pm)
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;

  return `${month}.${day}.${year} ${displayHours}:${minutes} ${ampm}`;
}

export const Step5DeliveryRecipients: React.FC<Step5DeliveryRecipientsProps> = ({
  recipients,
  inviteName,
  completedAt,
  onBack,
  onDone,
  onUndoComplete,
  isUndoing = false,
  sentEmailHtml,
  sentEmailSubject,
  guestLinkUrl,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeTab, setActiveTab] = useState<PreviewTab>('email');

  // Filter and sort recipients
  const filteredRecipients = useMemo(() => {
    let result = [...recipients];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(query) ||
          (r.title && r.title.toLowerCase().includes(query)) ||
          r.organization.toLowerCase().includes(query) ||
          r.email.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'organization':
          aValue = a.organization.toLowerCase();
          bValue = b.organization.toLowerCase();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return result;
  }, [recipients, searchQuery, sortField, sortDirection]);

  // Toggle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Name', 'Title', 'Organization', 'Email'];
    const rows = filteredRecipients.map((r) => [
      r.name,
      r.title || '',
      r.organization,
      r.email,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${inviteName.replace(/[^a-zA-Z0-9]/g, '_')}_recipients.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-dark-muted" />;
    }
    return (
      <ArrowUpDown
        className={`w-3 h-3 ${
          sortDirection === 'asc' ? 'text-cyan-400' : 'text-cyan-400 rotate-180'
        }`}
      />
    );
  };

  // Group by organization for stats
  const orgCount = useMemo(() => {
    const orgs = new Set(recipients.map((r) => r.organization));
    return orgs.size;
  }, [recipients]);

  // Check if we have any preview content
  const hasEmailPreview = !!sentEmailHtml;
  const hasGuestUrl = !!guestLinkUrl;
  const hasAnyPreview = hasEmailPreview || hasGuestUrl;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-dark-border/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                Invite Completed
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                  {recipients.length} delivered
                </span>
              </h3>
              <p className="text-xs text-dark-muted flex items-center gap-2">
                <span>{inviteName}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(completedAt)}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Subject line - always visible */}
        {sentEmailSubject && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-dark-bg/50 rounded-xl border border-dark-border/30">
            <FileText className="w-4 h-4 text-purple-400 shrink-0" />
            <span className="text-xs text-dark-muted">Subject:</span>
            <span className="text-sm text-white font-medium truncate">
              {sentEmailSubject}
            </span>
          </div>
        )}

        {/* Stats bar */}
        <div className="flex items-center gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1.5 text-dark-muted">
            <Users className="w-3.5 h-3.5 text-blue-400" />
            <span>{recipients.length} recipients</span>
          </div>
          <div className="flex items-center gap-1.5 text-dark-muted">
            <Building2 className="w-3.5 h-3.5 text-purple-400" />
            <span>{orgCount} organizations</span>
          </div>
          <div className="flex items-center gap-1.5 text-dark-muted">
            <Mail className="w-3.5 h-3.5 text-cyan-400" />
            <span>{recipients.filter((r) => r.email).length} with email</span>
          </div>
        </div>
      </div>

      {/* Main content area - split view with tabs */}
      <div className={`flex-1 overflow-hidden flex ${hasAnyPreview ? 'flex-row' : 'flex-col'}`}>
        {/* Preview Panel with Tabs */}
        {hasAnyPreview && (
          <div className="w-1/2 border-r border-dark-border/30 flex flex-col overflow-hidden">
            {/* Tab buttons */}
            <div className="flex border-b border-dark-border/30 bg-dark-bg/50">
              {hasEmailPreview && (
                <button
                  onClick={() => setActiveTab('email')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-150 border-b-2 ${
                    activeTab === 'email'
                      ? 'text-cyan-400 border-cyan-400 bg-cyan-500/5'
                      : 'text-dark-muted border-transparent hover:text-white hover:bg-dark-surface/50'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  Email Preview
                </button>
              )}
              {hasGuestUrl && (
                <button
                  onClick={() => setActiveTab('guestUrl')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-150 border-b-2 ${
                    activeTab === 'guestUrl'
                      ? 'text-cyan-400 border-cyan-400 bg-cyan-500/5'
                      : 'text-dark-muted border-transparent hover:text-white hover:bg-dark-surface/50'
                  }`}
                >
                  <Link2 className="w-4 h-4" />
                  Guest URL Events
                </button>
              )}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
              {/* Email Preview Tab */}
              {activeTab === 'email' && hasEmailPreview && (
                <div className="h-full overflow-auto bg-dark-surface">
                  <iframe
                    srcDoc={sentEmailHtml}
                    title="Sent Email Preview"
                    className="w-full h-full min-h-[400px]"
                    style={{ border: 'none' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              )}

              {/* Guest URL Events Tab */}
              {activeTab === 'guestUrl' && hasGuestUrl && (
                <div className="h-full flex flex-col">
                  {/* URL header with copy/open buttons */}
                  <div className="p-3 border-b border-dark-border/30 bg-dark-bg/30">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span className="text-xs text-dark-muted truncate flex-1" title={guestLinkUrl}>
                        {guestLinkUrl}
                      </span>
                      <a
                        href={guestLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 text-xs text-cyan-400 hover:text-cyan-300 transition-all duration-150"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </a>
                    </div>
                  </div>
                  {/* Guest URL iframe */}
                  <div className="flex-1 overflow-auto bg-dark-surface">
                    <iframe
                      src={guestLinkUrl}
                      title="Guest URL Events"
                      className="w-full h-full min-h-[400px]"
                      style={{ border: 'none' }}
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recipients Panel */}
        <div className={`flex flex-col overflow-hidden ${hasAnyPreview ? 'w-1/2' : 'flex-1'}`}>
          {/* Search and export */}
          <div className="p-3 border-b border-dark-border/30 flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, title, or organization..."
                className="w-full pl-10 pr-4 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500"
              />
            </div>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-3 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-dark-muted hover:text-white hover:border-dark-border/30 transition-all duration-150"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>

          {/* Recipients table */}
          <div className="flex-1 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg border-b border-dark-border/30">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider cursor-pointer hover:text-white transition-all duration-150"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider cursor-pointer hover:text-white transition-all duration-150"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {getSortIcon('title')}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider cursor-pointer hover:text-white transition-all duration-150"
                    onClick={() => handleSort('organization')}
                  >
                    <div className="flex items-center gap-1">
                      Organization
                      {getSortIcon('organization')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-dark-muted text-sm">
                      {searchQuery ? 'No recipients match your search' : 'No recipients recorded'}
                    </td>
                  </tr>
                ) : (
                  filteredRecipients.map((recipient) => (
                    <tr
                      key={recipient.personId}
                      className="border-b border-dark-border/30 hover:bg-dark-surface/50 transition-all duration-150"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <span className="text-sm text-white">{recipient.name}</span>
                          <span className="block text-xs text-dark-muted truncate max-w-[200px]">
                            {recipient.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-dark-muted">
                          {recipient.title || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-dark-muted">{recipient.organization}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-dark-border/30 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-dark-muted hover:text-white transition-all duration-150 text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          <span className="text-xs text-dark-muted">
            Showing {filteredRecipients.length} of {recipients.length}
          </span>

          {/* Undo Complete button */}
          {onUndoComplete && (
            <button
              onClick={onUndoComplete}
              disabled={isUndoing}
              className="flex items-center gap-2 px-4 py-2 bg-dark-bg border border-amber-500/50 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/10 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Revert to draft status"
            >
              <Undo2 className="w-4 h-4" />
              {isUndoing ? 'Reverting...' : 'Undo Complete'}
            </button>
          )}

          <button
            onClick={onDone}
            className="px-5 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-all duration-150"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step5DeliveryRecipients;
